/**
 * One customer, everything about them, on one screen (redesign phase 2).
 *
 * ── The problem this solves ─────────────────────────────────────────────
 *
 * Configuring one paying customer used to mean visiting three screens and
 * finding that customer in a picker twice: caps and models on the tenant table,
 * per-tenant pricing behind a search box on Feature Pricing, and the plan they
 * are on nowhere at all. Nothing anywhere said "here is customer X".
 *
 * ── Why tabs rather than one long page ──────────────────────────────────
 *
 * The sections answer different questions asked at different times — "what are
 * they spending" (support), "what may they spend" (commercial), "what do we
 * charge them" (pricing). Stacking them would rebuild the wall of controls the
 * tenant table already was, one scroll instead of one row. The tab is also the
 * unit of future work: Plan & billing, People and Activity land in later
 * phases and slot in without redesigning anything.
 *
 * The tab is deliberately NOT in the URL. The customer is (`.../customers/<id>`)
 * because "look at this customer" is what gets pasted into a ticket; which tab
 * they had open is not, and putting it in the path would make every link three
 * segments deep for no gain.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from '@papan/i18n'
import { useToast, useConfirm, DirtyBar, useRefreshWhenShown, useBillingCurrency, usePlanEditBar } from '@papan/core-ui/ui'
import { formatMoney, normalisePlanRateLimits } from '@papan/types'
import {
  budgetApi, type AdminTenantRow,
} from '../../client/budget'
import { catalogAdminApi, type AdminPlanView } from '@papan/billing/client'
import { DisplayCurrencyEditor } from './DisplayCurrencyEditor'
import {
  CustomerPlan, TenantPromotionsPanel, PlanEditor,
} from '@papan/billing/ui'
import { tenantPlanSavers } from './tenantPlanSavers'
import { pinnedBatch } from '@papan/api-client'
import { CustomerPeople } from './CustomerPeople'
import { CustomerModelAccess } from './CustomerModelAccess'
import { ChatCostVisibilityPicker } from './ChatCostVisibilityPicker'
import { CustomerPersonalMode } from './CustomerPersonalMode'
import { CustomerPermissions } from './CustomerPermissions'
import { SubscriptionHistory } from './SubscriptionHistory'
import { CustomerFundedApps } from './CustomerFundedApps'
import { CustomerPdapps } from './CustomerPdapps'
import { AuditLog } from './AuditLog'
import { formatBytes, Section, FeatureBreakdown, ReadOnlyNotice } from './bits'
import { HealthBadge, UploadsPausedChip, IdentityOnlyChip, EnforcementChip, capState } from './customerBits'

type Tab = 'overview' | 'plan' | 'billing' | 'people' | 'config' | 'pdapps' | 'permissions' | 'activity'

/**
 * The tabs, in the order an operator reads a customer: what they are doing,
 * what they pay, who they are, what they are ALLOWED, who may do what, and what
 * has been done to them.
 *
 * SIX for an organization, five for a personal tenant — Permissions is the one
 * that varies, see below.
 *
 * ── Why Configuration is one tab and not four ───────────────────────────
 *
 * It shipped as eight, with Limits, Pricing, Models and Currency each holding a
 * single card. That made the strip wide enough to scroll horizontally, and —
 * worse for the thing this redesign exists to fix — it made "what can I
 * actually configure about this customer?" a question you could only answer by
 * clicking four tabs and remembering. The four are one question, so they are
 * one screen, and an operator who opens it sees the whole answer at once.
 *
 * The sections keep their own headings and their own read-only notices, which
 * is what lets them sit together despite Pricing being gated on a DIFFERENT
 * permission: a `member` seat sees three editable sections and one that says,
 * in place, that re-pricing is a price-book grant. Side by side, that teaches
 * the boundary. Four tabs deep, it just looked like something was broken.
 *
 * ── Why Permissions IS a tab, and the four collapsed ones were not ───────
 *
 * `d0e90f29c` folded Limits, Pricing, Models and Currency into Configuration
 * because each held a SINGLE CARD, so "what can I configure about this
 * customer?" was only answerable by clicking four tabs and remembering. That
 * argument is about size, and it runs the other way here: the permission grid is
 * 32 capabilities across 10 sections with a column per role — larger than the
 * whole of Configuration put together, and wide enough to need its own
 * horizontal scroll. Inline, it pushed Display currency, chat-cost visibility,
 * the launcher and per-tenant pricing so far down that Configuration stopped
 * answering its own question.
 *
 * So this is not a reversal of that consolidation. The rule it followed —
 * a tab earns its place by holding a screen's worth of one subject — is the
 * same rule that gives this one a tab.
 *
 * ORGANIZATIONS ONLY (D-4), and the TAB is hidden, not just its content. Role
 * defaults describe what a member holding a role may do, and a personal tenant
 * has no members — a tab that opens onto an explanation of why it is empty is
 * worse than no tab, because it is a click that teaches nothing.
 */
const ALL_TABS: ReadonlyArray<{ id: Tab; labelKey: string; fallback: string; orgOnly?: true }> = [
  { id: 'overview', labelKey: 'settings.platform.customer.tab.overview', fallback: 'Overview' },
  { id: 'plan', labelKey: 'settings.platform.customer.tab.plan', fallback: 'Plan' },
  { id: 'billing', labelKey: 'settings.platform.customer.tab.billing', fallback: 'Billing' },
  { id: 'people', labelKey: 'settings.platform.customer.tab.people', fallback: 'People' },
  { id: 'config', labelKey: 'settings.platform.customer.tab.config', fallback: 'Configuration' },
  { id: 'pdapps', labelKey: 'settings.platform.customer.tab.pdapps', fallback: 'PDApps' },
  { id: 'permissions', labelKey: 'settings.platform.customer.tab.permissions', fallback: 'Permissions', orgOnly: true },
  { id: 'activity', labelKey: 'settings.platform.customer.tab.activity', fallback: 'Activity' },
]

export function CustomerDetail({
  tenantId,
  canEdit,
  canEditPricing,
  canEditSettings,
  launcherSection,
  onBack,
}: {
  tenantId: string
  /** `customers:write` — caps, quota, models, currency. */
  canEdit: boolean
  /**
   * `pricing:write`, SEPARATE from `canEdit` on purpose.
   *
   * Per-tenant feature pricing renders on this page but it is RE-PRICING, and
   * the capability split exists so that administering a customer does not imply
   * re-pricing the product. Gating it by where it renders rather than by what
   * it does would hand every `member` seat exactly the authority phase 0
   * withheld.
   */
  canEditPricing: boolean
  /**
   * `settings:write`, SEPARATE from both of the above (0174).
   *
   * The chat-cost control renders on this page and is gated by the SETTINGS
   * area, because what it decides is who may see the platform's own cost of
   * goods — the same subject as the deployment-wide setting, merely scoped to
   * one tenant. Third instance of "gate by what the action does, not where it
   * renders"; without it a `member` seat (`customers: 'write'`,
   * `settings: 'read'`) would reach the same outcome one tenant at a time.
   */
  canEditSettings: boolean
  /**
   * The app-launcher curation panel for THIS customer (D6).
   *
   * A slot, not an import: `LauncherAdmin` lives in `@papan/launcher`, which
   * `@papan/orgs` may not depend on. The console composes the two — the same
   * shape as the metered-pricing grid inside the price book.
   *
   * Absent on hosts that do not supply it (mobile), where the Configuration tab
   * says so rather than rendering a silent gap.
   */
  launcherSection?: ReactNode
  onBack: () => void
}) {
  const { t } = useTranslation('core-ui')
  const billingCurrency = useBillingCurrency()
  const toast = useToast()
  const [row, setRow] = useState<AdminTenantRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState<Tab>('overview')

  /**
   * The tabs this tenant actually has. `row` is null while loading, so an
   * org-only tab stays hidden until the type is KNOWN — appearing a beat late is
   * the safe direction, where flashing a Permissions tab onto a personal tenant
   * is not.
   */
  const tabs = useMemo(
    () => ALL_TABS.filter((tb) => !tb.orgOnly || row?.type === 'organization'),
    [row?.type],
  )

  /**
   * Derived, never an effect. This component does NOT remount between customers
   * (`load` re-fetches on `tenantId`), so an operator sitting on Permissions who
   * opens a personal tenant would otherwise hold a selected tab that no longer
   * exists — `aria-controls` pointing at nothing and an empty panel. Deriving
   * the fallback renders correctly on the FIRST frame; an effect would paint the
   * broken state once and then fix it, and under `<StrictMode>` would run twice.
   */
  const activeTab: Tab = tabs.some((tb) => tb.id === tab) ? tab : 'overview'

  const busy = saving || !canEdit

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const found = await budgetApi.getTenant(tenantId)
      if (!found) {
        setError(t('settings.platform.customer.notFound', {
          defaultValue: 'That tenant no longer exists.',
        }))
        setRow(null)
      } else {
        setRow(found)
        setError(null)
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load tenant')
    } finally {
      setLoading(false)
    }
  }, [tenantId, t])

  useEffect(() => { load() }, [load])

  // The deployment's billing mode, so the seat control shows the right thing:
  // under MANUAL billing the tenant's INCLUDED seats are the ceiling (a grant),
  // under provider it is the max-seats ceiling. Fail-soft to 'provider' — a
  // caller without `settings:read` still edits everything else; provider is the
  // current shipped default, so the max-seats control is the safe fallback.
  const [manualBilling, setManualBilling] = useState(false)
  useEffect(() => {
    // Needed wherever the SEAT control renders — that is the Plan tab (included
    // vs max seats, and which axis a save writes). Fetching only on Configuration
    // meant opening Plan first left `manualBilling` false, so the control showed
    // "Max seats" and a save wrote `max_seats_override` — a dead axis under
    // manual billing, where `resolveSeatCap` reads `included_seats`.
    if (activeTab !== 'plan' && activeTab !== 'config') return
    budgetApi.getPlatformSettings()
      .then(r => setManualBilling(r.resolved.planAuthority === 'manual'))
      .catch(() => setManualBilling(false))
  }, [tab])

  // The tenant's EFFECTIVE plan, fetched so Section 2's PlanEditor can render
  // the plan's values as inheritance placeholders. `null` = not yet loaded;
  // Section 2's editor half shows a loading line until it resolves. A null or
  // stale `planId` resolves to the FREE plan, never to null — see the effect
  // below. `tenantPlanError` (Task 7, G7) is the SEPARATE signal a fetch
  // FAILED — Section 2's caps now render INSIDE `PlanEditor`, which mounts
  // only once `tenantPlan` resolves, so without this a failed fetch left the
  // whole section stranded on a permanent "Loading" with no way out.
  const [tenantPlan, setTenantPlan] = useState<AdminPlanView | null>(null)
  const [tenantPlanError, setTenantPlanError] = useState(false)
  useEffect(() => {
    if (activeTab !== 'plan' || !row) { setTenantPlan(null); setTenantPlanError(false); return }
    let live = true
    setTenantPlanError(false)
    catalogAdminApi.listPlans()
      .then(r => {
        if (!live) return
        const match = row.planId ? r.plans.find(p => p.id === row.planId) : undefined
        // A null or stale `planId` is the FREE floor, which carries
        // `planId: null` meaning "the free plan" — NOT "no plan" (CLAUDE.md,
        // plan ladder). Any reader of that layer must substitute the free plan
        // itself (`FREE_PLAN_ID === 'free'`, a stable seeded id), or the whole
        // PlanEditor half of Section 2 blanks to a perpetual "loading".
        setTenantPlan(match ?? r.plans.find(p => p.id === 'free') ?? null)
      })
      .catch(() => { if (live) { setTenantPlan(null); setTenantPlanError(true) } })
    return () => { live = false }
  }, [tab, row?.planId])

  // ── A13 / A15: the tenant's OWN uploads + rate-limit overrides ────────────
  //
  // `PlanUploads` and `PlanRateLimitsEditor` (rendered by `<PlanEditor>` in
  // tenant scope) read their current value from `scope.plan.features` — but
  // that is the shared PRESET's value, not this tenant's override. So a saved
  // edit appeared to vanish on reload: the uploads toggle snapped back to the
  // plan's "allowed", and the rate-limit field fell back to the plan's default
  // shown in its hint. Overlay the tenant's resolved values onto the plan
  // handed to those two controls, exactly as the seat/storage/concurrency axes
  // already read `row.*Override`. The SAVE paths are untouched — this only
  // fixes what the fields DISPLAY.
  const tenantEffectivePlan = useMemo<AdminPlanView | null>(() => {
    if (!tenantPlan) return null
    return {
      ...tenantPlan,
      features: {
        ...(tenantPlan.features ?? {}),
        // `planBlocksCreate` tests `=== false`; mirror the tenant's resolved
        // freeze so an off↔on edit sticks after the reload.
        canCreateContent: row?.uploadsPaused ? false : true,
        // The editor's VALUES are this tenant's OWN overrides ONLY — NOT merged
        // with the plan. `resolveTenantRateLimit` falls through per tier, so a
        // blank tier must stay blank (saved as inherited); the plan's own limits
        // are threaded separately as `rateLimitBaseline` (the placeholder). A
        // whole-object merge here would materialise the plan's inherited tiers
        // into explicit tenant overrides on the next save.
        rateLimits: row?.rateLimits ?? undefined,
      },
    }
  }, [tenantPlan, row?.uploadsPaused, row?.rateLimits])

  // The PLAN's OWN rate limits — the inheritance baseline `PlanRateLimitsEditor`
  // shows for a blank tenant tier (what it inherits + is enforced at), so the
  // field never advertises the platform default the tenant does not inherit
  // through. Read from `tenantPlan` (the raw preset), NOT `tenantEffectivePlan`,
  // whose `features.rateLimits` now carries the tenant's overrides.
  const tenantRateLimitBaseline = useMemo(
    () => normalisePlanRateLimits(tenantPlan?.features?.rateLimits) ?? undefined,
    [tenantPlan],
  )

  // ── The Plan tab's SINGLE Save bar ──────────────────────────────────
  //
  // The Plan tab mirrors the price book's plan editor: several sections
  // (`<PlanEditor>`'s scalars/tenantScalars/tenantCaps/tenantEnforcement,
  // model access, AI model prices, metered pricing) under ONE Save/Discard.
  // Each registers into this controller — the ceilings/seats/enforcement
  // block that used to be hand-built HERE (`'limits'`) is now rendered
  // INSIDE `<PlanEditor>` itself (Task 7), registering its own bar sections
  // the same way every other `<PlanEditor>` section already does. No section
  // reloads mid-batch — the controller reloads ONCE after `saveAll`, so a
  // sibling's still-unsaved draft is never re-seeded out from under it.
  // (LlmPricing / FeaturePricingEditor self-refresh their OWN rows, which
  // never touch `row`.)
  const { bar, total: barTotal, saveAll, discardAll } = usePlanEditBar()

  /**
   * Re-fetch when the deck comes back on screen — but NOT while edits are staged.
   *
   * The desktop shell keeps a deck MOUNTED behind `display: none`, so `load`
   * above would otherwise run exactly once per browser tab, and this page would
   * serve whatever was true when the shelf was first opened. See
   * `DeckVisibility`.
   *
   * `barTotal` alone is the guard (Task 7 folded the last locally-staged draft
   * — the ceilings/seats/enforcement block — into `<PlanEditor>`'s own bar
   * sections, so every staged edit on this tab now registers into the SAME
   * bar and a clean `barTotal` means genuinely nothing is staged anywhere on
   * the tab, not just in a block CustomerDetail happens to own itself).
   */
  useRefreshWhenShown(() => { if (barTotal === 0) void load() })

  const runPlanSave = async () => {
    setSaving(true)
    // #1316 review round 2: ONE batch for the Save — every section writes as the identity that pressed it, and nothing
    // is shown or reloaded once that session ended
    const batch = pinnedBatch()
    try {
      await saveAll(batch)
      if (batch.ended()) return
      await load()
      toast.success(t('settings.budget.saved'))
    } catch (e: any) {
      if (batch.ended()) return
      toast.error(e?.message || 'Save failed')
      await load()
    } finally {
      setSaving(false)
    }
  }

  if (loading && !row) return <div className="p-4 themed-text-muted text-sm">{t('common:loading')}</div>
  if (error && !row) {
    return (
      <div>
        <BackLink onBack={onBack} />
        <div className="p-4 text-sm text-red-400">{error}</div>
      </div>
    )
  }
  if (!row) return null

  const moneyOpts = { currency: billingCurrency }

  // ── The display-currency second line is GONE ─────────────────────────
  //
  // It rendered "~RM55.89" beneath the primary figure, converting a canonical
  // USD amount at the tenant's display rate. Both halves of that stopped being
  // true: the primary figure is now the BILLING-native charge, so converting
  // it again by a display rate is a second conversion of an amount that is
  // already in the customer's own currency — a plausible number nobody is
  // charged. The display layer is parked (plan section 7), not deleted, so the
  // fields stay on the wire; nothing renders them here.
  const money = { fmt: (m: number) => formatMoney(m, moneyOpts), note: undefined as string | undefined }
  const cap = capState(row)

  return (
    <div>
      <BackLink onBack={onBack} />

      {/* Persistent header: who, on what plan, how healthy, spending what
          against what. Stays put across tabs because it is the context every
          tab is read in. */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
        <div className="min-w-0">
          {/* The badges sit BESIDE the heading, not inside it. ScrollSpy builds
              its "on this page" label from the heading's textContent, so a chip
              nested here rendered as "Budget Demo CoEnforce" in the rail. */}
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="themed-text text-lg font-medium truncate">{row.name}</h2>
            <HealthBadge row={row} />
            <UploadsPausedChip row={row} />
            <IdentityOnlyChip row={row} />
            <EnforcementChip mode={row.enforcementMode} />
          </div>
          <div className="text-[11px] themed-text-muted font-mono mt-0.5 flex items-center gap-2 flex-wrap">
            <span>{row.id}</span>
            <span>·</span>
            <span>{row.type}</span>
            <span>·</span>
            <span>
              {/* What to SHOW (0164): the preset's name, "Custom", or the
                  operator's own label. `planName` stays the PRESET's name and is
                  surfaced separately below when they differ, because "which
                  preset is this bespoke customer based on" is a real question a
                  single conflated string cannot answer. */}
              {row.planLabel ?? row.planName ?? '—'}
              {/* A plan the payment provider has never heard of must not look
                  identical to one it bills for — see `planSource` (0157). */}
              {row.planSource === 'override' && (
                <span className="ml-1 themed-text-muted">
                  ({t('settings.platform.customer.planSetHere', { defaultValue: 'set here' })})
                </span>
              )}
            </span>
            {/* Only when the displayed name is NOT the preset's, so a standard
                customer does not carry a redundant "based on Default". */}
            {row.isCustom && row.planName && (
              <>
                <span>·</span>
                <span title={t('settings.platform.customer.basedOnHint', {
                  defaultValue: 'Hand-edited since it was created. Preset updates skip it by default.',
                })}>
                  {t('settings.platform.customer.basedOn', {
                    defaultValue: 'based on {{preset}}', preset: row.planName,
                  })}
                </span>
              </>
            )}
            <span>·</span>
            <span>{t('settings.platform.customers.memberCount', {
              defaultValue: '{{count}} member',
              defaultValue_other: '{{count}} members',
              count: row.memberCount,
            })}</span>
            {row.seatCount != null && (
              <>
                <span>·</span>
                {/* Billed seats vs actual members. `seat_count` is a CACHE kept
                    by seat-sync, so a mismatch is a silent revenue leak one way
                    and an overcharge the other — worth showing, not hiding. */}
                <span
                  className={row.seatCount !== row.memberCount ? 'text-amber-300' : undefined}
                  title={row.seatCount !== row.memberCount
                    ? t('settings.platform.customer.seatDrift', {
                        defaultValue: 'Billed seats disagree with the member count.',
                      })
                    : undefined}
                >
                  {t('settings.platform.customer.seats', {
                    defaultValue: '{{n}} billed seats',
                    n: row.seatCount,
                  })}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="themed-text text-lg font-medium tabular-nums">{formatMoney(row.periodCostBillingMicro, moneyOpts)}</div>
          <div className="text-[11px] themed-text-muted mt-0.5">
            {cap.total != null
              ? t('settings.platform.customers.ofCap', { defaultValue: 'of {{cap}}', cap: formatMoney(cap.total, moneyOpts) })
              : cap.blocked
                ? t('settings.platform.customers.blocked', { defaultValue: 'AI blocked' })
                : t('settings.budget.unlimited', { defaultValue: 'No ceiling' })}
          </div>
        </div>
      </div>

      {/* C4: a zero cap stops AI SPEND and nothing else — files, sharing and
          mini-apps keep working. Saying "suspended" would claim a capability
          this product does not have. */}
      {cap.blocked && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 mt-3">
          <div className="text-red-300 text-xs font-medium">
            {t('settings.platform.customer.blockedTitle', { defaultValue: 'AI is blocked for this tenant' })}
          </div>
          <p className="themed-text-secondary text-xs mt-0.5">
            {t('settings.platform.customer.blockedBody', {
              defaultValue:
                'Both spend ceilings are set to zero, so AI requests are refused. Files, sharing and PDApps are unaffected — this is not a suspension.',
            })}
          </p>
        </div>
      )}

      {/* The write freeze, surfaced (manual billing phase 3). Its own banner
          rather than a line in the one above, because the two states are
          ORTHOGONAL — a customer can be AI-blocked, uploads-paused, both or
          neither — and both banners appearing together is the honest rendering of
          "both". The copy carries the same three facts the 402 does: what stopped,
          what still works, and what fixes it. */}
      {row.uploadsPaused && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 mt-3">
          <div className="text-amber-300 text-xs font-medium">
            {t('settings.platform.customer.pausedTitle', { defaultValue: 'Uploads are paused for this tenant' })}
          </div>
          <p className="themed-text-secondary text-xs mt-0.5">
            {t('settings.platform.customer.pausedBody', {
              defaultValue:
                'Their plan blocks new files and folders. They keep full read access to everything they own and can still edit it — this is not a suspension. Moving them to a plan that allows new items lifts it, within a minute.',
            })}
          </p>
        </div>
      )}

      {!canEdit && <div className="mt-3"><ReadOnlyNotice /></div>}

      {/* Tabs.
          `overflow-y-hidden` is load-bearing, not tidying. CSS computes an
          `overflow-y: visible` to `auto` the moment the other axis is not
          visible, so `overflow-x-auto` alone gives this strip a scrollable Y
          axis too — and the `-mb-px` on each tab (which pulls the active
          underline onto the container's own border) leaves the content box 1px
          taller than the padding box. One pixel of overflow is enough for a
          full-height vertical scrollbar to appear beside five tabs that fit. */}
      {/* A real tablist (F-23).
          ────────────────────────────────────────────────────────────────
          These were five plain buttons: no `role`, no `aria-selected`, no
          `aria-controls`, and no arrow-key navigation — so a screen-reader user
          heard five unlabelled buttons with nothing saying which was active or
          that they controlled a panel, and a keyboard user tabbed through all
          five to reach the content.

          The roving tabindex is the part that is easy to omit: only the ACTIVE
          tab is in the tab order, so Tab moves from the strip to the panel
          rather than through four siblings, and Left/Right move between tabs.
          That is what the pattern specifies, and it is why `tabIndex` is
          computed rather than left at its default. */}
      <div
        role="tablist"
        aria-label={t('settings.platform.customer.tabsLabel', { defaultValue: 'Tenant sections' })}
        className="flex items-center gap-1 mt-4 mb-4 border-b themed-border overflow-x-auto overflow-y-hidden"
        onKeyDown={e => {
          // Over the VISIBLE list, not `ALL_TABS` — arrowing through a tab that
          // is not rendered would select a panel with no tab to focus.
          const i = tabs.findIndex(x => x.id === activeTab)
          const next = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1
            : e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : null
          if (next === null) return
          e.preventDefault()
          const target = tabs[(next + tabs.length) % tabs.length]
          setTab(target.id)
          // Focus follows selection, per the tabs pattern — otherwise the arrow
          // key changes the panel while focus stays on a tab that is no longer
          // the selected one.
          document.getElementById(`customer-tab-${target.id}`)?.focus()
        }}
      >
        {tabs.map(tb => (
          <button
            key={tb.id}
            id={`customer-tab-${tb.id}`}
            role="tab"
            aria-selected={activeTab === tb.id}
            aria-controls={`customer-panel-${tb.id}`}
            tabIndex={activeTab === tb.id ? 0 : -1}
            onClick={() => setTab(tb.id)}
            className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
              activeTab === tb.id
                ? 'border-blue-500 themed-text'
                : 'border-transparent themed-text-muted hover:themed-text'
            }`}
          >
            {t(tb.labelKey, { defaultValue: tb.fallback })}
          </button>
        ))}
      </div>

      {/* The panel the tablist controls. One wrapper for whichever tab is
          active — `aria-controls` above has to point somewhere, and a panel per
          tab would mean four empty ones in the a11y tree. */}
      <div
        role="tabpanel"
        id={`customer-panel-${activeTab}`}
        aria-labelledby={`customer-tab-${activeTab}`}
        tabIndex={-1}
      >

      {activeTab === 'overview' && (
        <Section
          title={t('settings.platform.customer.spendTitle', { defaultValue: 'Spend this period' })}
          desc={t('settings.platform.customer.spendDesc', {
            defaultValue: 'Every metered surface, not just AI.',
          })}
        >
          <div className="themed-bg-secondary rounded-lg p-3">
            <div className="flex items-baseline justify-between gap-2 mb-2">
              <span className="text-xs themed-text-muted">
                {row.periodMessages} {t('settings.budget.messages')}
              </span>
              <span className="themed-text font-medium tabular-nums">{formatMoney(row.periodCostBillingMicro, moneyOpts)}</span>
            </div>
            <FeatureBreakdown rows={row.featureBreakdown} money={money} />
          </div>

          <CustomerFundedApps tenantId={row.id} canEdit={canEdit} />

          {/* F-35 — storage used to be a CARD here as well as an editable field
              on Configuration, framed differently in each and with neither
              naming the other.

              It is stated once now, on Configuration, and this is the pointer.
              Which way round is not a coin toss: storage is quota-ENFORCED and
              never metered (CLAUDE.md, migration 0171), so it has no place in a
              spend total — a card sitting under "Every metered surface" implied
              it was one of them. Utilisation belongs beside the ceiling it is
              checked against, which is exactly where the quota is set. */}
          <p className="themed-text-muted text-xs mt-3">
            {t('settings.platform.customer.storageElsewhere', {
              defaultValue: 'Storage is not metered — it is a quota.',
            })}{' '}
            <button
              onClick={() => setTab('config')}
              className="underline underline-offset-2 hover:themed-text transition-colors"
            >
              {t('settings.platform.customer.storageElsewhereLink', {
                defaultValue: 'See how full their drive is, and change the quota, on Configuration.',
              })}
            </button>
          </p>
        </Section>
      )}

      {activeTab === 'billing' && (
        <>
          {/* Billing-tab reduction (2026-08-26) — two sections only, with NO
              tab-intro heading/paragraph (the tab is already labelled
              "Billing"; each section carries its own title + info tooltip):
                1. SubscriptionHistory   — the tenant's subscription history
                                           (self-sectioned): a per-term table
                                           with expandable payment rows, plus a
                                           folded "Add subscription" form.
                2. TenantPromotionsPanel — per-tenant promotions (its own
                                           "Promotions" heading + info tooltip).
              The old ExtraGraceHistory / CustomerAccount / CreditLedgerHistory /
              CreditAdjustPanel blocks and the manual-billing paid-through /
              fallback / contract-price editors were removed; the non-payment
              fallback still lives on the Operations manual-billing queue. */}
          <SubscriptionHistory
            tenantId={row.id}
            canEdit={canEdit}
            onOpenPlan={() => setTab('plan')}
            onPlanChanged={load}
          />

          {/* Per-customer money action on the same `customers:write` grant as
              the rest of this page; with the tenant fixed it cannot land a
              grant on the wrong org. The cross-customer half — redeemable codes
              and campaign revoke — lives on Operations. */}
          <div className="mt-6">
            <TenantPromotionsPanel tenantId={row.id} tenantName={row.name} canEdit={canEdit} />
          </div>
        </>
      )}

      {activeTab === 'people' && <CustomerPeople tenantId={row.id} canEdit={canEdit} />}

      {activeTab === 'pdapps' && <CustomerPdapps tenantId={row.id} canEdit={canEdit} />}

      {/* Permission defaults — TEMPORARY, relocated from the org's own
          Settings → Permissions (see `CustomerPermissions`).
          ────────────────────────────────────────────────────────────────
          Its own tab rather than a section on Configuration: the grid is 32
          capabilities × 5 role columns across 10 sections, which is more than
          the rest of that tab put together and buried everything below it. The
          reasoning is on `ALL_TABS`.

          Next to People, not under it: People is WHO they are, this is what a
          role may DO. Reading them side by side is the point — the roles named
          in the column headings here are the ones assigned there.

          `canEdit` is the `customers` gate, like its neighbours. The tab itself
          is organizations-only, so this never renders for a personal tenant —
          but the guard stays, because a tab list and a panel body agreeing is
          not something one of them should have to assume about the other. */}
      {activeTab === 'permissions' && row.type === 'organization' && (
        <Section
          title={t('settings.platform.customer.permissionsTitle', { defaultValue: 'Permission defaults' })}
          desc={t('settings.platform.customer.permissionsDesc', {
            defaultValue:
              'What each role may do inside this organization. Owners are always allowed. Changing these is temporarily done here rather than by the customer’s own owner.',
          })}
        >
          <CustomerPermissions tenantId={row.id} canEdit={canEdit} />
        </Section>
      )}

      {/* `billingCurrency` below is load-bearing, not decoration. It is needed by
          `manual_payment` AND `contract_price` — the two actions whose amounts are
          minor units of the charged currency rather than USD microdollars. Without
          it `describeAuditEntry` has no `currencyMinor` formatter, and both degrade:
          `manual_payment` SKIPS the amount entirely, so the one screen answering
          "what did this customer actually pay, and when" rendered the date and
          dropped the money; `contract_price` falls back to the raw JSON diff, which
          rendered "150000 flat" where the truth is MYR 1,500.00. Both were caught by
          reading a real row, not by typechecking. */}
      {activeTab === 'activity' && (
        <AuditLog
          tenantId={row.id}
          billingCurrency={billingCurrency}
          title={t('settings.platform.customer.activityTitle', { defaultValue: 'Activity' })}
          desc={t('settings.platform.customer.activityDesc', {
            defaultValue: 'Every cap, price, currency, model and credit change recorded for this tenant, with who made it.',
          })}
        />
      )}

      {/* Plan and Configuration share this block; each SECTION is gated to its
          tab. The Plan tab mirrors the price book's plan editor (assignment,
          identity, ceilings, models, pricing) under ONE Save bar; Configuration
          keeps the non-plan settings (personal mode, display currency, chat cost,
          launcher). */}
      {(activeTab === 'plan' || activeTab === 'config') && (
       <>
        {/* Section 1 (redesign): the collapsible preset section — preset name /
            Custom, the plan dropdown + Apply, and the read-only overwritten-values
            list with per-axis Remove. `CustomerPlan` now renders INSIDE it. */}
        {activeTab === 'plan' && <PlanIdentity row={row} canEdit={canEdit} onChanged={load} />}

        {/* Personal-tenant MODE (identity-only vs full) — a drive-access
            decision, not a plan grant, so it stays in Configuration. */}
        {activeTab === 'config' && <CustomerPersonalMode row={row} canEdit={canEdit} onSaved={load} />}

        {activeTab === 'plan' && (
        <Section
          title={t('settings.platform.customer.section2Title', { defaultValue: 'Plan settings for this tenant' })}
          info={t('settings.platform.customer.section2Desc', {
            defaultValue:
              'Everything the plan defines, for this tenant. A blank field inherits the plan; a value here overrides it, and the plan’s own value is shown alongside. Mirrors the plan editor — the plan itself is chosen above.',
          })}
        >
          {/* Everything the plan defines — the REUSABLE `PlanEditor`, in
              `tenant` scope (P4 Task 2; ceilings/seats/enforcement folded in
              at Task 7). Replaces the old hand-built read-only mirror
              (uploads / billing rhythm / request limits) AND the bespoke
              free/extra/grace-cap + seat-ceiling + enforcement-mode block
              that used to render HERE: `<PlanEditor>`'s tenant branch now
              renders the caps as `InheritedCapField`s in its Row 6, the seat
              ceiling in its Row 1 (alongside storage/concurrency), and
              enforcement mode as its own plain `<select>` — each registering
              its OWN bar section (`tenantCaps` / `tenantScalars` /
              `tenantEnforcement`) into the SAME `bar` as `PlanIdentity`
              (Section 1) and everything else below, so one Save commits all
              of it. `savers` is the injected `tenantPlanSavers` adapter
              (`@papan/orgs`, closing over `budgetApi`) — `@papan/billing`
              cannot import it directly (see `plan-editor-scope.ts`'s module
              docblock). No currency selector: the tenant price-set writes
              `currency = billingCurrency`, always. `!canEditPricing` still
              gets its own read-only notice here, since `PlanEditor` renders
              no orgs-specific copy of its own. */}
          {!canEditPricing && (
            <ReadOnlyNotice
              note={t('settings.platform.customer.pricingReadOnly', {
                defaultValue:
                  'Changing what a tenant is charged is a price-book permission, which your role does not include — even though you can administer this tenant.',
              })}
            />
          )}
          {tenantPlanError
            ? (
              <p className="themed-text-muted text-sm mt-4 text-red-400">
                {t('settings.platform.customer.planLoadError', {
                  defaultValue: 'Could not load this tenant’s plan. Try switching tabs, or reload the page.',
                })}
              </p>
              )
            : tenantPlan
            ? (
              <PlanEditor
                scope={{
                  kind: 'tenant',
                  tenantId: row.id,
                  // The TENANT's name, for the per-tenant controls' accessible
                  // names — not the plan's, which is shared across tenants.
                  tenantName: row.name,
                  // A13/A15: the DISPLAY plan carries this tenant's own uploads
                  // + rate-limit overrides; the seat `inherited` below still
                  // reads the raw preset (`tenantPlan.*`) on purpose.
                  plan: tenantEffectivePlan ?? tenantPlan,
                  // A15: the PLAN's own rate limits, so a blank tenant tier
                  // shows what it inherits + is enforced at (per-tier), not the
                  // platform default. `plan.features.rateLimits` above carries
                  // the tenant's OWN overrides; these are the baseline beneath.
                  rateLimitBaseline: tenantRateLimitBaseline,
                  storageQuotaBytes: row.storageQuotaBytes,
                  agentConcurrencyCap: row.agentConcurrencyCap,
                  allowedModels: row.allowedModels,
                  // Free/extra/grace (Task 6, P4) — `.saved` from this
                  // tenant's own override record (Task 4), `.inherited` from
                  // the resolved preset's own caps. `<PlanEditor>`'s
                  // `tenantCaps` bar section (Task 7) renders these; its
                  // `tenantScalars` section renders seat/storage/concurrency.
                  freeCap: {
                    saved: row.overrides?.free?.value ?? null,
                    inherited: row.presetCaps?.freeCapBillingMicro ?? null,
                  },
                  extraCap: {
                    saved: row.overrides?.extra?.value ?? null,
                    inherited: row.presetCaps?.extraCapBillingMicro ?? null,
                  },
                  graceCap: {
                    saved: row.overrides?.grace?.value ?? null,
                    inherited: row.presetCaps?.graceCapBillingMicro ?? null,
                  },
                  platformGraceCeiling: row.platformGraceCeiling ?? 0,
                  // `mode` mirrors `<PlanEditor>`'s own seat control (its Row
                  // 1) — a personal tenant has no seat concept at all, and
                  // manual billing makes included-seats the ceiling rather
                  // than max-seats. `inherited` reads the PLAN's own value
                  // for whichever axis is live, never `presetCaps` (which
                  // carries no seat fields).
                  seat: row.type !== 'organization'
                    ? { mode: 'none', saved: null, inherited: null }
                    : manualBilling
                      ? { mode: 'included', saved: row.includedSeatsOverride ?? null, inherited: tenantPlan.includedSeats }
                      : { mode: 'max', saved: row.maxSeatsOverride ?? null, inherited: tenantPlan.maxSeats },
                  enforcementMode: row.enforcementMode,
                }}
                savers={tenantPlanSavers(row.id, billingCurrency)}
                bar={bar}
                busy={saving}
                canEdit={canEdit}
                canEditPricing={canEditPricing}
                planAuthority={manualBilling ? 'manual' : 'provider'}
                // Unused by tenant scope (no currency selector renders), kept
                // empty rather than threading the deployment's currency list
                // just to satisfy the prop's type.
                currencies={[]}
                defaultCurrency={billingCurrency}
                draft={undefined}
                onDraftChange={() => {}}
                // NO reload here — the Plan tab's aggregate bar reloads once
                // after every section writes (a mid-batch reload would
                // re-seed a sibling section's still-unsaved draft, e.g. the
                // caps `<PlanEditor>` itself is staging). Matches the old
                // ModelAccessEditor/LlmPricing/FeaturePricingEditor wiring
                // this replaces.
                onSaved={async () => {}}
                onSaveTenantAllowedModels={async (m) => { await budgetApi.setAllowedModels(row.id, m) }}
                resolvedModelAccessSlot={<CustomerModelAccess tenantId={row.id} />}
                storageUseSlot={
                  <>
                    <StorageUse used={row.storageUsedBytes} quota={row.storageQuotaBytes} staged={false} />
                    <EngineScratch scratchBytes={row.scratchBytes} />
                  </>
                }
                saveBarTotal={0}
                saveBarBusy={false}
                onSaveAll={() => {}}
                onDiscardAll={() => {}}
                t={t}
              />
              )
            : <p className="themed-text-muted text-sm mt-4">{t('common:loading')}</p>}
        </Section>
        )}

        {activeTab === 'config' && (
        <Section
          title={t('settings.platform.customer.currencyTitle', { defaultValue: 'Display currency' })}
          desc={t('settings.platform.customer.currencyDesc', {
            defaultValue:
              'PARKED — this setting currently changes nothing on any screen. Every money '
              + 'figure now renders in the deployment\'s billing currency, which is what the '
              + 'tenant is actually charged in. The setting is kept (and still writes its '
              + 'audited epoch) so the preference survives for a later developer-facing use; '
              + 'it is not a way to re-denominate this tenant.',
          })}
        >
          <div className="themed-bg-secondary rounded-lg p-3">
            <DisplayCurrencyEditor
              tenantId={row.id}
              tenantName={row.name}
              currency={row.displayCurrency}
              rate={row.displayCurrencyRate}
              rateSource={row.displayCurrencyRateSource}
              derivedRate={row.derivedDisplayCurrencyRate}
              periodCostBillingMicro={row.periodCostBillingMicro}
              disabled={busy}
              onSaved={load}
            />
          </div>
        </Section>
        )}

        {/* Beside Display currency rather than beside the ceilings, because it
            is the same KIND of thing: how money is RENDERED for this tenant,
            not how much of it they may spend. It changes no ceiling, no bill,
            and no spending screen — only the figures inside the chat window.

            Gated on `canEditSettings`, not `canEdit`. See the prop's docblock;
            the read-only notice renders in place for the same reason the
            pricing one does, so a `member` seat learns where the boundary runs
            by seeing it beside what they CAN change. */}
        {activeTab === 'config' && (
        <Section
          title={t('settings.platform.customer.chatCostTitle', { defaultValue: 'Cost shown in the AI assistant' })}
          desc={t('settings.platform.customer.chatCostDesc', {
            defaultValue:
              'Overrides the deployment-wide setting for this tenant only. Choosing what the platform pays discloses nothing to the customer — their own members always see what they are charged.',
          })}
        >
          <div className="themed-bg-secondary rounded-lg p-3">
            {!canEditSettings && (
              <ReadOnlyNotice
                note={t('settings.platform.customer.chatCostReadOnly', {
                  defaultValue:
                    'Changing this needs the platform-settings permission — it decides who can see the platform\u2019s own provider cost, not this customer\u2019s configuration.',
                })}
              />
            )}
            <ChatCostVisibilityPicker
              tenantId={row.id}
              stored={row.chatCostVisibility ?? null}
              disabled={saving || !canEditSettings}
              onSaved={load}
            />
          </div>
        </Section>
        )}

        {activeTab === 'config' && (
        <>
        {/* D6: the launcher, WITHOUT its own tenant picker.
            ────────────────────────────────────────────────────────────────
            The Launcher destination keeps its picker — an operator arriving
            there is asking "which customer?". An operator already on THIS page
            has answered that, and sending them to another screen to pick the
            same customer again is the "three screens and two tenant pickers"
            antipattern the redesign exists to remove.

            Inside Configuration rather than as a sixth TAB, deliberately. The
            strip went from eight tabs to five by grouping one question per tab,
            and "what is this customer allowed?" is the question Configuration
            already answers — ceilings, models, currency, and now which apps they
            see. A sixth tab would undo that for a single panel.

            Gated by the blanket `requirePlatformAdmin` its routes carry, not by
            the capability matrix (CLAUDE.md records that as #337's deferral), so
            no capability prop is threaded here.

            Injected as a SLOT rather than imported: this component lives in
            `@papan/orgs` and `LauncherAdmin` in `@papan/launcher`, which orgs
            may not depend on. The console composes the two — the same shape as
            the metered-pricing grid inside the price book, and for the same
            reason. */}
        <Section
          title={t('settings.platform.customer.launcherTitle', { defaultValue: 'App launcher' })}
          desc={t('settings.platform.customer.launcherDesc', {
            defaultValue: 'Which PDApps this tenant sees, and in what order.',
          })}
        >
          {launcherSection ?? (
            <p className="themed-text-muted text-sm">
              {t('settings.platform.customer.launcherUnavailable', {
                defaultValue: 'App curation is not available on this viewport.',
              })}
            </p>
          )}
        </Section>
        </>
        )}

        {/* The Plan tab's SINGLE Save/Discard — every section above registered
            into `bar`. Sticky, announced, and present only when something is
            staged. Mirrors the price book's plan editor. */}
        {/* `canEdit || canEditPricing`: the pricing sections register into this
            bar under `canEditPricing`, so a role that ever holds pricing:write
            without customers:write would otherwise stage price edits into a bar
            with no Save button. No such role exists today; this decouples it. */}
        {activeTab === 'plan' && (canEdit || canEditPricing) && (
          <DirtyBar
            count={barTotal}
            busy={saving}
            onSave={runPlanSave}
            onDiscard={discardAll}
            pendingLabel={t('settings.platform.customer.unsaved', {
              defaultValue: '{{count}} unsaved change',
              defaultValue_other: '{{count}} unsaved changes',
              count: barTotal,
            })}
            discardLabel={t('settings.platform.customer.discard', { defaultValue: 'Discard' })}
            saveLabel={t('common:save')}
            savingLabel={t('common:saving')}
          />
        )}
       </>
      )}
      </div>
    </div>
  )
}

/**
 * How full the tenant's drive is, against the quota in the field beside it.
 *
 * Reads the STAGED quota, not the saved one — the same rule the ≈ line on the
 * money caps follows, and for the same reason: the preview an operator checks
 * their typed number against has to be about the typed number. A bar that kept
 * showing the old percentage until Save would be worse than no bar.
 *
 * Over 100% is a REAL state and is drawn as one: shrinking a quota below what
 * a tenant already holds is permitted (it stops new uploads, it does not delete
 * anything), so the operator has to be able to see that they have just done it.
 */
function StorageUse({ used, quota, staged }: {
  used: number | null
  quota: number
  staged: boolean
}) {
  const { t } = useTranslation('core-ui')
  if (used == null) {
    return <span className="text-[11px] themed-text-muted ml-2">{t('settings.budget.notMeasured')}</span>
  }
  const pct = quota > 0 ? (used / quota) * 100 : null
  const over = pct != null && pct > 100
  return (
    <span className="inline-flex items-center gap-2 ml-2 align-middle">
      <span className={`text-[11px] tabular-nums ${over ? 'text-amber-300' : 'themed-text-muted'}`}>
        {t('settings.budget.usedOf', { used: formatBytes(used) })}
        {pct != null && ` (${pct < 1 && pct > 0 ? '<1' : Math.round(pct)}%)`}
      </span>
      {pct != null && (
        <span className="inline-block h-1 w-24 rounded-full themed-bg-tertiary overflow-hidden align-middle">
          <span
            className={`block h-full rounded-full ${
              pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-blue-500'
            }`}
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </span>
      )}
      {over && staged && (
        <span className="text-[11px] text-amber-300">
          {t('settings.platform.customer.storageBelowUsed', {
            defaultValue: 'Below what they already hold — no new uploads until they free space.',
          })}
        </span>
      )}
    </span>
  )
}

/**
 * Engine "scratch" bytes (#535 §2b, Task 2.3) — app-db working-set space the
 * Phase 2 survey measures per tenant. Rendered as its OWN labelled line,
 * never merged into `StorageUse` above it: that line is the counted,
 * quota-enforced figure, and this one explicitly is not — folding scratch
 * into the same sentence would blur exactly the distinction the label exists
 * to state. Hidden at 0 (never surveyed, or genuinely nothing scratched) so
 * the common case — most tenants have none — doesn't add a permanent zero
 * line to every row.
 */
function EngineScratch({ scratchBytes }: { scratchBytes: number | undefined }) {
  const { t } = useTranslation('core-ui')
  if (!scratchBytes) return null
  return (
    <span className="block text-[11px] themed-text-muted mt-1">
      {t('settings.budget.engineScratch', { used: formatBytes(scratchBytes) })}
    </span>
  )
}

function BackLink({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation('core-ui')
  return (
    <button
      onClick={onBack}
      className="inline-flex items-center gap-1.5 text-xs themed-text-muted hover:themed-text transition-colors mb-3"
    >
      <span aria-hidden>←</span>
      {t('settings.platform.customer.back', { defaultValue: 'All tenants' })}
    </button>
  )
}

/**
 * The customer's plan identity: which preset, whether it is bespoke, its name,
 * and the way back to standard.
 *
 * Renders on a STANDARD customer too, quietly — "never hand-edited" is worth
 * stating, because the alternative is an operator inferring it from the absence
 * of a badge. It only grows controls once there is something to control.
 */
function PlanIdentity({
  row, canEdit, onChanged,
}: {
  row: AdminTenantRow
  canEdit: boolean
  onChanged: () => void
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const confirm = useConfirm()
  const [busy, setBusy] = useState(false)
  const [naming, setNaming] = useState(false)
  const [open, setOpen] = useState(false)
  const [draftName, setDraftName] = useState(row.planLabel ?? '')

  const billingCurrency = useBillingCurrency()

  // ── The read-only list of OVERWRITTEN values, each with a per-row Remove ──
  //
  // A ceiling axis is overwritten when the tenant's resolved value diverges from
  // the preset's; an explicit `allowed_models` list overrides model access.
  // "Remove" returns that ONE axis to inheriting the plan (state 'inherit'),
  // distinct from "Put back on the preset" which clears everything at once.
  type OverItem = { key: string; label: string; cur: string; pre: string; remove: () => Promise<void> }
  const norm = (v: number | null | undefined) => (v == null ? null : Number(v))

  async function clearOverride(fn: () => Promise<unknown>) {
    setBusy(true)
    try {
      await fn()
      onChanged()
      toast.success(t('settings.platform.customer.overrideRemoved', { defaultValue: 'Override removed.' }))
    } catch (e: any) {
      toast.error(e?.message || 'Failed to remove the override.')
    } finally { setBusy(false) }
  }

  const overItems: OverItem[] = []
  if (row.presetCaps) {
    const c = row.presetCaps
    const money = (v: number | null) => (v == null ? t('catalog.unlimited', { defaultValue: 'unlimited' }) : formatMoney(v, { currency: billingCurrency }))
    const count = (v: number | null) => (v == null ? t('settings.platform.customer.defaultVal', { defaultValue: 'default' }) : String(v))
    // Dedicated labels (no `{{currency}}` interpolation): the value column already
    // carries the currency, and reusing the newOrg keys rendered a raw
    // "Included budget ({{currency}})" because the locale string expects a param.
    const CEILINGS = [
      { axis: 'free' as const, label: t('settings.platform.customer.owBudget', { defaultValue: 'Included budget' }),
        cur: row.freeCapBillingMicro, pre: c.freeCapBillingMicro, fmt: money },
      { axis: 'extra' as const, label: t('settings.platform.customer.owOverage', { defaultValue: 'Overage cap' }),
        cur: row.extraCapBillingMicro, pre: c.extraCapBillingMicro, fmt: money },
      { axis: 'storage' as const, label: t('settings.platform.customer.owStorage', { defaultValue: 'Storage quota' }),
        cur: row.storageQuotaBytes, pre: c.storageQuotaBytes, fmt: (v: number | null) => (v == null ? '—' : formatBytes(v)) },
      { axis: 'concurrency' as const, label: t('settings.platform.customer.owConcurrency', { defaultValue: 'Concurrent agent jobs' }),
        cur: row.agentConcurrencyCap, pre: c.agentConcurrencyCap, fmt: count },
    ]
    for (const a of CEILINGS) {
      if (norm(a.cur) === norm(a.pre)) continue
      const inherit =
        a.axis === 'free' ? { free: { state: 'inherit' as const } }
        : a.axis === 'extra' ? { extra: { state: 'inherit' as const } }
        : a.axis === 'storage' ? { storage: { state: 'inherit' as const } }
        : { concurrency: { state: 'inherit' as const } }
      overItems.push({
        key: a.axis,
        label: a.label,
        cur: a.fmt(norm(a.cur)),
        pre: a.fmt(norm(a.pre)),
        remove: () => clearOverride(() => budgetApi.setTenantOverrides(row.id, inherit)),
      })
    }
  }
  if (row.allowedModels && row.allowedModels.length > 0) {
    overItems.push({
      key: 'models',
      label: t('settings.platform.customer.owModels', { defaultValue: 'Model access' }),
      cur: t('settings.platform.customer.owModelsN', { defaultValue: '{{count}} allowed', count: row.allowedModels.length }),
      pre: t('settings.platform.customer.owModelsAll', { defaultValue: 'all (inherit)' }),
      remove: () => clearOverride(() => budgetApi.setAllowedModels(row.id, null)),
    })
  }

  async function saveName() {
    setBusy(true)
    try {
      await budgetApi.setPlanLabel(row.id, draftName.trim() || null)
      setNaming(false)
      onChanged()
      toast.success(t('settings.platform.customer.nameSaved', { defaultValue: 'Name saved.' }))
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save the name.')
    } finally { setBusy(false) }
  }

  async function reset() {
    const ok = await confirm({
      title: t('settings.platform.customer.resetTitle', { defaultValue: 'Put this tenant back on the preset?' }),
      // ── This copy said the OPPOSITE until 2026-08-18 ──────────────────
      //
      // It promised "their current limits stay exactly as they are", which was
      // true while Custom was a stored MARKER the old route cleared. Custom is
      // DERIVED now — from the override rows themselves — so there is no way to
      // stop being Custom while keeping hand-set limits: the limits ARE what
      // makes a tenant Custom. `returnTenantToPlan` discards them and
      // re-projects the preset's.
      //
      // A confirmation that understates its blast radius is worse than none,
      // and this one has no undo. It now names every family it clears and says
      // the values are replaced.
      message: t('settings.platform.customer.resetBody', {
        defaultValue:
          'This DISCARDS their hand-set limits and puts the preset’s values back — along with any model restrictions, per-tenant prices and the custom name. Preset updates will reach them again. There is no undo; the previous values remain readable on the Activity tab.',
      }),
      confirmText: t('settings.platform.customer.resetOk', { defaultValue: 'Discard and put back on the preset' }),
    })
    if (!ok) return
    setBusy(true)
    try {
      const r = await budgetApi.returnToPlan(row.id)
      onChanged()
      // Reports what was actually cleared rather than a bare "done": the whole
      // point of the confirmation above is that this changes values, so the
      // result should say how much it changed.
      toast.success(t('settings.platform.customer.resetDone', {
        defaultValue: 'Back on the preset — {{axes}} ceiling(s) and {{pricing}} price override(s) cleared.',
        axes: r.clearedAxes.length,
        pricing: r.clearedFeaturePricing,
      }))
    } catch (e: any) {
      toast.error(e?.message || 'Failed to put the tenant back on the preset.')
    } finally { setBusy(false) }
  }

  return (
    <Section
      title={t('settings.platform.customer.identityTitle', { defaultValue: 'Plan' })}
      info={row.isCustom
        ? t('settings.platform.customer.identityCustomDesc', {
            defaultValue:
              'Hand-edited since it was created, so preset updates skip this tenant by default. Their limits are below.',
          })
        : t('settings.platform.customer.identityPresetDesc', {
            defaultValue:
              'Never hand-edited, so preset updates apply to this tenant. That is not a check that every number still matches.',
          })}
    >
      <div className="themed-bg-secondary rounded-lg p-3">
        {/* Collapsed header: preset name / Custom + "based on" + overwritten count. */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            aria-expanded={open}
            className="min-w-0 text-left flex items-center gap-2"
          >
            <span className="min-w-0">
              <span className="themed-text text-sm block">{row.planLabel ?? row.planName ?? '—'}</span>
              {row.isCustom && row.planName && (
                <span className="themed-text-muted text-[11px] block">
                  {t('settings.platform.customer.basedOn', { defaultValue: 'based on {{preset}}', preset: row.planName })}
                </span>
              )}
            </span>
            <span className="themed-text-muted text-xs">{open ? '▲' : '▼'}</span>
          </button>
          {overItems.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded themed-bg-tertiary themed-text-muted">
              {t('settings.platform.customer.owCount', { defaultValue: '{{count}} overwritten', count: overItems.length })}
            </span>
          )}
        </div>

        {open && (
          <div className="mt-3 space-y-3 border-t themed-border pt-3">
            {/* Plan assignment — the dropdown + Apply, inside the collapsible
                section (redesign: "on expand, a dropdown to select the linked
                plan and an Apply button"). */}
            <CustomerPlan tenantId={row.id} canEdit={canEdit} onChanged={onChanged} />

            {/* Read-only list of every overwritten value, each with a per-axis
                Remove that returns just that axis to inheriting the plan. */}
            {overItems.length > 0 && (
              <div>
                <div className="themed-text-secondary text-xs mb-1">
                  {t('settings.platform.customer.overwrittenTitle', { defaultValue: 'Overwritten for this tenant' })}
                </div>
                <div className="themed-bg-primary rounded border themed-border">
                  {overItems.map((it) => (
                    <div key={it.key} className="flex items-center gap-2 px-2.5 py-1.5 border-b themed-border last:border-b-0">
                      <span className="min-w-0 flex-1">
                        <span className="themed-text text-xs block truncate">{it.label}</span>
                        <span className="themed-text-muted text-[10px] block truncate">
                          {it.cur}
                          <span className="opacity-70"> · {t('settings.platform.customer.planDefaultIs', { defaultValue: 'plan default {{v}}', v: it.pre })}</span>
                        </span>
                      </span>
                      {canEdit && (
                        <button
                          className="themed-btn-secondary text-[10px] px-2 py-1 shrink-0"
                          disabled={busy}
                          onClick={() => void it.remove()}
                        >
                          {t('settings.platform.customer.removeOverride', { defaultValue: 'Remove' })}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Name / reset actions. */}
            <div className="flex items-center gap-2 flex-wrap">
              {canEdit && row.isCustom && !naming && (
                <button className="themed-btn-secondary text-xs px-2 py-1" disabled={busy}
                  onClick={() => { setDraftName(row.planLabel ?? ''); setNaming(true) }}>
                  {row.planLabel
                    ? t('settings.platform.customer.rename', { defaultValue: 'Rename' })
                    : t('settings.platform.customer.nameIt', { defaultValue: 'Name this arrangement' })}
                </button>
              )}
              {canEdit && naming && (
                <div className="flex items-center gap-2">
                  <input
                    className="themed-input text-xs px-2 py-1 w-48"
                    value={draftName}
                    maxLength={60}
                    autoFocus
                    placeholder={t('settings.platform.customer.namePlaceholder', { defaultValue: 'e.g. Acme Enterprise' })}
                    aria-label={t('settings.platform.customer.nameIt', { defaultValue: 'Name this arrangement' })}
                    onChange={e => setDraftName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') void saveName(); if (e.key === 'Escape') setNaming(false) }}
                  />
                  <button className="themed-btn-primary text-xs px-2 py-1" disabled={busy} onClick={() => void saveName()}>
                    {t('settings.platform.customer.saveName', { defaultValue: 'Save' })}
                  </button>
                  <button className="themed-btn-secondary text-xs px-2 py-1" disabled={busy} onClick={() => setNaming(false)}>
                    {t('settings.platform.customer.discard', { defaultValue: 'Discard' })}
                  </button>
                </div>
              )}
              {canEdit && row.isCustom && !naming && (
                <button className="themed-btn-secondary text-xs px-2 py-1" disabled={busy} onClick={() => void reset()}>
                  {t('settings.platform.customer.resetAction', { defaultValue: 'Put back on the preset' })}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </Section>
  )
}
